/***************************************************
 * This file contains isolated piece of computation so that it can be used within
 * a web worker, this is why we need so specific imports in order to reduce the worker file size.
 ***************************************************/
import { intersection, isEmpty, keys, sortBy } from "lodash-es";
import moment from "moment";

import type { ColumnAnnotation, DataRow, DataSeries, DatasetColumn, StringMapEntry } from "../graphql.js";
import type { ICustomDatasetColumn, IDuration, SelectedDataSeries } from "../redux/modules/dataset-viewer.js";
import { ANNOTATION_TYPE, CHART_TYPE, DURATION_UNIT } from "../settings/settings.js";
import type {
  ICondition,
  IConditionCombination,
  IDataSeriesData,
  IDataSeriesDataMap,
  IDatasetChartProps,
} from "../ui-modules/analytical-viewer/DatasetChart.js";

export function getDataSeriesLineKey(seriesName: string, lineIndex: number) {
  return `${getDataSeriesKey(seriesName)}line${lineIndex}`;
}

export function getDataSeriesKey(seriesName: string) {
  // change all characters except numbers and letters
  return seriesName.replace(/[^a-zA-Z0-9]/g, "_");
}

export function parseDuration(durationExpression: string): IDuration {
  // parse leading number part of expression
  const value: number = Number.parseInt(durationExpression, 10);
  if (isNaN(value)) {
    return { value: Number.NaN };
  }

  // determine unit value for parsing duration with moment.js
  const unitValue: any = durationExpression.substr(value.toString().length).trim().toLowerCase();

  let unit: string;
  if (unitValue.startsWith("s")) {
    unit = DURATION_UNIT.SECONDS;
  } else if (unitValue.startsWith("m")) {
    unit = DURATION_UNIT.MINUTES;
  } else if (unitValue.startsWith("h")) {
    unit = DURATION_UNIT.HOURS;
  } else if (unitValue.startsWith("d")) {
    unit = DURATION_UNIT.DAYS;
  } else if (unitValue.startsWith("w")) {
    unit = DURATION_UNIT.WEEKS;
  } else if (unitValue.startsWith("mo")) {
    unit = DURATION_UNIT.MONTHS;
  } else if (unitValue.startsWith("y")) {
    unit = DURATION_UNIT.YEARS;
  } else {
    return { value: Number.NaN };
  }

  return { value, unit };
}

export function parseDurationAsSeconds(durationExpression: string): number {
  const parsedDuration: IDuration = parseDuration(durationExpression);
  if (!parsedDuration.unit) {
    return Number.NaN;
  }
  // return parsedDuration in seconds
  return moment.duration(parsedDuration.value, parsedDuration.unit as any).asSeconds();
}

export function calculateChartData(chartProps: IDatasetChartProps): IConditionCombination[] {
  // reverse the dataseries so that the first is draw last and is on top in case of overlapping lines
  chartProps.selectedDataSeries =
    chartProps.layout.chartType === CHART_TYPE.LINE
      ? chartProps.selectedDataSeries.reverse()
      : chartProps.selectedDataSeries;

  // create the set of combinations of conditions
  let chartData: IConditionCombination[] = getConditionsCombinations(chartProps);
  // add corresponding data values for each of the selected data series
  addDataSeriesValues(chartData, chartProps);
  // remove condition combinations without any data in the data series map
  if (chartProps.layout.filterOutEmptyValues) {
    chartData = filterOutConditionsWithoutData(chartData);
  }

  // consolidation of value e.g. calculate the average value
  consolidateValues(chartData, chartProps);

  // format for the API
  formatDataRecords(chartData, chartProps);

  return chartData;
}

/**
 * create the set of combinations of conditions/annotation depending on the user selection
 */

function getConditionsCombinations(chartProps: IDatasetChartProps): IConditionCombination[] {
  let combinations: IConditionCombination[] = [];

  // build the set of combinations of conditions from the selected annotations
  for (let a = 0; a < chartProps.selectedAnnotations.length; a++) {
    const annotation: ColumnAnnotation = chartProps.selectedAnnotations[a];
    const newCombinations: IConditionCombination[] = [];

    const uniqueAnnotationValues: IUniqueAnnotationValue[] = collectUniqueAnnotationValues(annotation);

    for (let v = 0; v < uniqueAnnotationValues.length; v++) {
      const annotationValue: IUniqueAnnotationValue = uniqueAnnotationValues[v];

      const condition: ICondition = {
        value: annotationValue.value,
        label: annotation.name,
        key: annotation.field,
        multiValueIndices: annotationValue.multiValueIndices,
      };

      if (combinations.length > 0) {
        combinations.forEach((combination: IConditionCombination) => {
          // we add the new condition to the existing set, and create a new combination entry
          const newConditions: ICondition[] = combination.conditions.slice(0);

          newConditions.push(condition);
          newCombinations.push({ conditions: newConditions });
        });
      } else {
        // if there is no combination of conditions yet, we create the top level conditions set
        const newConditions: ICondition[] = [condition];
        newCombinations.push({ conditions: newConditions });
      }
    }

    combinations = newCombinations;
  }

  return combinations;
}

/**
 * add the raw values corresponding to the specified combination of conditions,
 * for each of the selected data series
 */
function addDataSeriesValues(combinations: IConditionCombination[], chartProps: IDatasetChartProps) {
  const isLogScale: boolean = chartProps.layout.isLogScale;
  let columns: DatasetColumn[] = chartProps.columns;
  // exclude some columns directly according to the filteredOutColumns
  if (chartProps.filteredOutColumns?.length) {
    columns = chartProps.columns.filter(
      (column: DatasetColumn) => !chartProps.filteredOutColumns.includes(column.field),
    );
  }

  combinations.forEach((combination: IConditionCombination) => {
    const conditions: ICondition[] = combination.conditions;
    const dataseries: IDataSeriesDataMap = {};
    const fieldIdentifiers: string[] = []; // for identifying the user selection

    // we filter out the data series that are split by annotation and order the other accordingly
    const orderedSelectedDataSeries: SelectedDataSeries[] = chartProps.selectedDataSeries;

    orderedSelectedDataSeries.forEach((selectedDataSeries: SelectedDataSeries) => {
      const dataSeriesName: string = selectedDataSeries.name;
      // if the data series has been generated via a split condition then we still get the data via the parent data series name
      const referenceDataSeriesName: string = selectedDataSeries.splitParent
        ? selectedDataSeries.splitParent
        : dataSeriesName;
      const splitByCondition: ICondition = selectedDataSeries.splitParent && {
        value: selectedDataSeries.splitValue,
        key: selectedDataSeries.splitByCondition,
        multiValueIndices:
          selectedDataSeries.splitMultiValueIndex != null ? [selectedDataSeries.splitMultiValueIndex] : [],
      };
      const conditionsWithSplitConditions: ICondition[] = splitByCondition
        ? conditions.concat([splitByCondition])
        : conditions;

      // compute the value index that will be used to filter applicable values in case of multi value data series
      const multiValueIndexFromConditions = getApplicableMultiValuesIndices(conditionsWithSplitConditions);

      const dataSeries: DataSeries = chartProps.dataSeriesMap[referenceDataSeriesName];

      if (dataSeries?.rows) {
        const valuesByConditions: { key: string; value: number }[] = [];

        // at this point columns are up to date wrt filtering
        columns // e.g. samples
          .filter((column: DatasetColumn) =>
            columnCorrespondsToSpecifiedConditions(column, conditionsWithSplitConditions),
          ) // if the current column corresponds to the set of selected conditions
          .forEach((column: DatasetColumn) => {
            let valuesFoundForThisColumn = false;
            // filtered values means the whole sub-row (values sharing the same valueIndex) should be excluded
            const excludedValueIndices = chartProps.filteredOutValueIndicesByColumn[column.field];
            // loop through all dataSeries rows to gather the ones corresponding to the column & filters
            dataSeries.rows.forEach((row: DataRow) => {
              const values = row.values
                .filter(
                  (entry) =>
                    entry.key === column.field &&
                    // if a value index applies to the conditions combination then we collect only applicable annotations (same or none)
                    (!multiValueIndexFromConditions?.length ||
                      !entry.multiValueIndex ||
                      multiValueIndexFromConditions.find((mvifc) => mvifc == entry.multiValueIndex)) &&
                    // make sure we also exclude the value that all share an excluded valueIndex by filtering (see filtering panel)
                    (!excludedValueIndices ||
                      !entry.multiValueIndex ||
                      !excludedValueIndices.includes(entry.multiValueIndex)),
                )
                // always get the value as a float
                .map((entry) => Number.parseFloat(entry.value))
                .filter(
                  (annotationValue: number) =>
                    ((annotationValue || annotationValue === 0) && !isLogScale) || (isLogScale && annotationValue > 0),
                )
                .map((annotationValue: number) => {
                  return { key: column.field, value: annotationValue };
                });
              if (values.length) {
                valuesFoundForThisColumn = true;
                valuesByConditions.push(...values);
              }
            });

            if (valuesFoundForThisColumn) {
              // store the field identifier
              fieldIdentifiers.push(column.field);
            }
          });

        if (valuesByConditions.length) {
          // Store the data series with a key that doesn't contain any dots!
          dataseries[getDataSeriesKey(dataSeriesName)] = { rawData: valuesByConditions };
        }
      }
    });

    combination.dataseries = dataseries;
    combination.fields = fieldIdentifiers;
  });
}

/**
 * remove condition combinations without any data to compress the chart
 * @param combinations
 * @return the filtered combination list
 */
function filterOutConditionsWithoutData(combinations: IConditionCombination[]): IConditionCombination[] {
  return combinations.filter((combination: IConditionCombination) => !isEmpty(combination.dataseries));
}

function getApplicableMultiValuesIndices(conditions: ICondition[]): string[] {
  // the valueIndex that will rule the picking of value must be the same for all condition combinations (or ignored if none)

  const multiValueIndicesForCombination: string[][] = conditions
    .filter((condition) => !!condition.multiValueIndices?.length)
    .map((condition) => condition.multiValueIndices);
  if (!multiValueIndicesForCombination.length) {
    return; // no multi value index involved, we can ignore it
  }
  const valueIndices: string[] = intersection<string>(...multiValueIndicesForCombination);
  if (!valueIndices.length) {
    // no intersection of value indices
    return ["-1"]; // incompatible combination of conditions where multi values cannot be grouped together
  }
  // if the same valueIndex (or more) applies to all conditions of this combination then we will pick the applicable values
  return valueIndices;
  /*

  const valueIndicesCount: number = valueIndices.length;
  if (valueIndicesCount > 1) {
  } else if (valueIndicesCount == 1) {
    return valueIndices[0];
  }

   */
}

/**
 * Average
 */
function mean(values: number[]): number {
  return values.reduce((sum, value) => sum + value) / values.length;
}

/**
 * Standard deviation, uncorrected
 * @see https://mathjs.org/docs/reference/functions/std.html
 */
function std(values: number[]): number {
  return Math.sqrt(mean(values.map((value) => (value - mean(values)) ** 2)));
}

/**
 * Natural logarithm
 */
function log(data: number | number[]): number | number[] {
  return Array.isArray(data) ? data.map((v) => Math.log(v)) : Math.log(data);
}

/**
 * Exponent
 */
function exp(data: number | number[]): number | number[] {
  return Array.isArray(data) ? data.map((v) => Math.exp(v)) : Math.exp(data);
}

/**
 * modify the data set to consolidate the data : calculate the average of the raw data points
 */
function consolidateValues(combinations: IConditionCombination[], chartProps: IDatasetChartProps) {
  const isLogScale: boolean = chartProps.layout.isLogScale;

  combinations.forEach((combination: IConditionCombination) => {
    const dataseries: IDataSeriesDataMap = combination.dataseries;

    Object.keys(dataseries).forEach((dataSeriesName) => {
      const dataSeriesData: IDataSeriesData = dataseries[dataSeriesName];

      // transform each data series data point to log-space if the log-setting is turned on
      const logData: number[] = transformForward(
        dataSeriesData.rawData.map((raw) => raw.value),
        isLogScale,
      ) as number[];

      // calculate the mean value of the values (which may have been log-transformed before)
      const meanLog: number = mean(logData);
      // calculate the empirical standard deviation of the values (which may have been log-transformed before)
      const stdLog: number = std(logData);
      // transform the mean value back to lin-space before we store it as condensed value
      const meanTransform: number = transformBackward(meanLog, isLogScale) as number;
      // upper and lower errors need to be provided as difference to the mean value, not in absolute values
      const errorLower: number = Math.abs((transformBackward(meanLog - stdLog, isLogScale) as number) - meanTransform);
      const errorUpper: number = Math.abs((transformBackward(meanLog + stdLog, isLogScale) as number) - meanTransform);

      // store the mean value and the error terms in lin-space because the y-axis scale already accounts for lin/log
      dataSeriesData.consolidatedValue = meanTransform;
      dataSeriesData.error = [errorLower, errorUpper];
    });
  });
}

function transformForward(valueOrArray: number | number[], toLog: boolean) {
  return toLog ? log(valueOrArray) : valueOrArray;
}

function transformBackward(valueOrArray: number | number[], isLog: boolean) {
  return isLog ? exp(valueOrArray) : valueOrArray;
}

/**
 * calculate the size of each inner group according to the number of corresponding buckets.
 * @param combinations
 * @param level
 * @param setInnerGroupSize
 */
function calculateInnerGroupSize(combinations: IConditionCombination[], level: number, setInnerGroupSize: any) {
  let lineIndex = 0;

  for (let i = 0; i < combinations.length; ) {
    const combination: IConditionCombination = combinations[i];
    const conditionValue: any = combination.conditions[level].value;
    let groupSize = 1;
    let endIndex: number = i + 1;

    for (let j = i + 1; j < combinations.length; j++, endIndex++) {
      if (combinations[j].conditions[level].value === conditionValue) {
        groupSize++;
      } else {
        break;
      }
    }

    // set inner group size for the items in the interval [i,endIndex)
    setInnerGroupSize(combinations, i, endIndex, groupSize, lineIndex);

    i = endIndex;
    lineIndex++;
  }
}

/**
 * Add auxiliary data structures to each data record
 */
function formatDataRecords(combinations: IConditionCombination[], chartProps: IDatasetChartProps) {
  combinations.forEach((combination: IConditionCombination) => {
    // the name of the point corresponds to the first condition value
    const conditionValue: string = combination.conditions[0].value;
    let nameValue: any = conditionValue;

    if (chartProps.layout.timeSeries) {
      if (chartProps.selectedAnnotations[0].type === ANNOTATION_TYPE.DURATION) {
        nameValue = parseDurationAsSeconds(conditionValue);
      } else if (chartProps.selectedAnnotations[0].type === ANNOTATION_TYPE.NUMERICAL) {
        nameValue = Number.parseFloat(conditionValue);
      }
    }

    combination.name = nameValue;
  });

  if (chartProps.selectedAnnotations.length > 1) {
    // calculate the size of each inner group according to the second
    calculateInnerGroupSize(combinations, 1, setFirstInnerGroupSize);

    // calculate secondInnerGroupSize and isSecondLeadingInner
    if (chartProps.selectedAnnotations.length > 2) {
      calculateInnerGroupSize(combinations, 2, setSecondInnerGroupSize);
    }
  } else {
    // set one big inner group for the case that only one condition is selected
    setFirstInnerGroupSize(combinations, 0, combinations.length, combinations.length, 0);
  }
}

function setFirstInnerGroupSize(
  combinations: IConditionCombination[],
  i: number,
  endIndex: number,
  groupSize: number,
  lineIndex: number,
) {
  for (let j = i; j < endIndex; j++) {
    const combination = combinations[j];

    combination.firstInnerGroupSize = groupSize;
    combination.isFirstLeadingInner = j === i;

    // the following creates artificially additional data series so that the line charts does not connects points that do not belong to the same conditions "level"
    const seriesToCopy: string[] = keys(combination.dataseries);

    seriesToCopy.forEach((name: string) => {
      combination.dataseries[getDataSeriesLineKey(name, lineIndex)] = combination.dataseries[name];
    });
  }
}

function setSecondInnerGroupSize(
  combinations: IConditionCombination[],
  i: number,
  endIndex: number,
  groupSize: number,
) {
  for (let j = i; j < endIndex; j++) {
    const combination = combinations[j];
    combination.secondInnerGroupSize = groupSize;
    combination.isSecondLeadingInner = j === i;
  }
}

export function columnCorrespondsToSpecifiedConditions(
  column: ICustomDatasetColumn,
  conditions: ICondition[],
): boolean {
  for (let k = 0; k < conditions.length; k++) {
    const condition: ICondition = conditions[k];
    const annotations: StringMapEntry[] = column.annotationsLookup[condition.key];

    const isMatching: boolean =
      column.name === condition.value || // this is to take sample names as a special kind of annotation
      !!annotations?.find(
        (annotation) =>
          annotation.value === condition.value &&
          // make sure the value index do match unless it is not defined
          (!condition.multiValueIndices?.length ||
            !annotation.multiValueIndex ||
            condition.multiValueIndices.find((mvi) => mvi == annotation.multiValueIndex)),
      );

    if (!isMatching) {
      return false;
    }
  }

  return true;
}

interface IUniqueAnnotationValue {
  value: string;
  multiValueIndices: string[]; // one single condition might apply to multi value indices of the same sample ! see SEL9-85
}

// the valueSet does not provide the corresponding value indices for each annotation value, therefore we compute first unique values of annotation.values instead
// TODO ideally we should get the annotation.valueSet as IUniqueAnnotationValue[] (or something similar) instead of string values so that we do not need to infer the valueIndex information from annotation.values and do the following (and take care of the reordering)
function collectUniqueAnnotationValues(annotation: ColumnAnnotation) {
  let uniqueAnnotationValues: IUniqueAnnotationValue[] = [];
  annotation.values.forEach((annotationValue) => {
    const indexOfExistingValue = uniqueAnnotationValues.findIndex((uav) => uav.value == annotationValue.value);
    if (indexOfExistingValue > -1) {
      if (annotationValue.multiValueIndex) {
        uniqueAnnotationValues[indexOfExistingValue].multiValueIndices.push(annotationValue.multiValueIndex);
      }
    } else {
      const uniqueAnnotationValue: IUniqueAnnotationValue = {
        value: annotationValue.value,
        multiValueIndices: annotationValue.multiValueIndex ? [annotationValue.multiValueIndex] : null,
      };
      uniqueAnnotationValues.push(uniqueAnnotationValue);
    }
  });

  // the ordering is driven by the annotation.valueSet listing
  uniqueAnnotationValues = sortBy(uniqueAnnotationValues, [
    (av) => annotation.valueSet.findIndex((value) => av.value == value),
  ]);

  return uniqueAnnotationValues;
}
